import React, { useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  Divider,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FiChevronDown,
  FiChevronUp,
  FiRefreshCw,
  FiClock,
} from "react-icons/fi";

const ATTEMPT_COLORS = ["#6366f1", "#f59e0b", "#10b981"];
const INTERVAL_HOURS = [3, 6, 9];
const INTERVAL_LABELS = [
  "3h after failure",
  "6h after failure",
  "9h after failure",
];

// Given the campaign, compute when attempt N will fire
const getNextRetryTime = (campaign, attemptIndex) => {
  const base = campaign.lastRetry
    ? new Date(campaign.lastRetry)
    : campaign.updatedOn
    ? new Date(campaign.updatedOn)
    : null;
  if (!base) return null;
  return new Date(
    base.getTime() + INTERVAL_HOURS[attemptIndex] * 60 * 60 * 1000
  );
};

const formatRelative = (date) => {
  if (!date) return null;
  const diff = date - Date.now();
  if (diff < 0) return "soon";
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 0) return `in ${h}h ${m}m`;
  return `in ${m}m`;
};

// ── Schedule row (shown when no history yet for that attempt) ──
const ScheduleRow = ({ attempt, campaign, isCurrent }) => {
  const color = ATTEMPT_COLORS[attempt - 1];
  const nextTime = getNextRetryTime(campaign, attempt - 1);

  return (
    <Box
      sx={{
        border: "1.5px dashed",
        borderColor: isCurrent ? color : "divider",
        borderRadius: 2,
        p: 2,
        bgcolor: isCurrent ? color + "08" : "background.default",
        opacity: isCurrent ? 1 : 0.5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            bgcolor: isCurrent ? color : "#e2e8f0",
            color: isCurrent ? "#fff" : "#94a3b8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {attempt}
        </Box>
        <Box flex={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="body2"
              fontWeight={600}
              color={isCurrent ? color : "text.secondary"}
            >
              Attempt #{attempt}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                · {INTERVAL_LABELS[attempt - 1]}
              </Typography>
            </Typography>
            {isCurrent ? (
              <Chip
                label="Pending"
                size="small"
                sx={{
                  bgcolor: color + "18",
                  color,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            ) : (
              <Chip
                label="Waiting"
                size="small"
                sx={{ bgcolor: "#f1f5f9", color: "#94a3b8", fontSize: 11 }}
              />
            )}
          </Stack>

          {isCurrent && nextTime && (
            <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
              <FiClock size={11} color={color} />
              <Typography variant="caption" color={color} fontWeight={600}>
                Scheduled {formatRelative(nextTime)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ·{" "}
                {nextTime.toLocaleString("en-IN", {
                  dateStyle: "short",
                  timeStyle: "short",
                })}
              </Typography>
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

// ── Resolved attempt row ──
const AttemptRow = ({ entry }) => {
  const { attempt, retriedAt, total, succeeded, failed, resolved } = entry;
  const color = ATTEMPT_COLORS[(attempt - 1) % 3];
  const successRate = total > 0 ? Math.round((succeeded / total) * 100) : 0;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        bgcolor: "background.default",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            bgcolor: color,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {attempt}
        </Box>
        <Box flex={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" fontWeight={600}>
              Attempt #{attempt}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                · {INTERVAL_LABELS[attempt - 1]}
              </Typography>
            </Typography>
            {!resolved ? (
              <Chip
                label="Pending"
                size="small"
                sx={{ bgcolor: "#fef3c7", color: "#92400e", fontSize: 11 }}
              />
            ) : (
              <Chip
                label="Resolved"
                size="small"
                sx={{ bgcolor: "#d1fae5", color: "#065f46", fontSize: 11 }}
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {new Date(retriedAt).toLocaleString("en-IN", {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </Typography>
        </Box>
      </Stack>

      <Box mb={1}>
        <LinearProgress
          variant="determinate"
          value={resolved ? successRate : 0}
          sx={{
            height: 6,
            borderRadius: 4,
            bgcolor: "#fee2e2",
            "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 4 },
          }}
        />
      </Box>

      <Stack direction="row" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#94a3b8",
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {total} total
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#10b981",
            }}
          />
          <Typography variant="caption" color="#10b981" fontWeight={600}>
            {resolved ? succeeded : "—"} sent
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#ef4444",
            }}
          />
          <Typography variant="caption" color="#ef4444" fontWeight={600}>
            {resolved ? failed : "—"} failed
          </Typography>
        </Stack>
        {resolved && (
          <Typography
            variant="caption"
            color={color}
            fontWeight={700}
            sx={{ ml: "auto !important" }}
          >
            {successRate}% recovered
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

const statusMeta = {
  in_progress: { label: "Retrying now…", color: "#6366f1", bg: "#ede9fe" },
  completed: { label: "All retries done", color: "#065f46", bg: "#d1fae5" },
  no_failures: { label: "No failures found", color: "#1e40af", bg: "#dbeafe" },
  default: { label: "Smart Retry On", color: "#92400e", bg: "#fef3c7" },
};

const SmartRetryStatus = ({ campaign }) => {
  const [open, setOpen] = useState(true); // open by default so schedule is visible

  if (!campaign?.smartRetry && !campaign?.retryHistory?.length) return null;

  const {
    smartRetryCount = 0,
    smartRetryStatus,
    retryHistory = [],
    lastRetry,
  } = campaign;

  const meta =
    statusMeta[smartRetryStatus] ??
    (campaign.smartRetry ? statusMeta.default : statusMeta.completed);

  const totalRecovered = retryHistory.reduce(
    (acc, r) => acc + (r.succeeded ?? 0),
    0
  );
  const totalRetried = retryHistory.reduce((acc, r) => acc + (r.total ?? 0), 0);

  // Which attempt indices have history already
  const doneAttempts = new Set(retryHistory.map((r) => r.attempt));

  // Build the full 3-attempt view — history OR schedule placeholder
  const allAttempts = [1, 2, 3].map((attempt) => {
    const historyEntry = retryHistory.find((r) => r.attempt === attempt);
    return { attempt, historyEntry };
  });

  return (
    <Box
      sx={{
        border: "1.5px solid",
        borderColor: meta.color + "55",
        borderRadius: 2.5,
        overflow: "hidden",
        mb: 2,
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ bgcolor: meta.bg, px: 2, py: 1.5, cursor: "pointer" }}
        onClick={() => setOpen((p) => !p)}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <FiRefreshCw size={15} color={meta.color} />
          <Typography variant="body2" fontWeight={700} color={meta.color}>
            Smart Retry
          </Typography>
          <Chip
            label={meta.label}
            size="small"
            sx={{
              bgcolor: meta.color + "22",
              color: meta.color,
              fontSize: 11,
              fontWeight: 600,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {smartRetryCount}/3 attempts
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          {totalRetried > 0 && (
            <Typography variant="caption" color="text.secondary">
              {totalRecovered}/{totalRetried} recovered
            </Typography>
          )}
          {lastRetry && (
            <Typography variant="caption" color="text.secondary">
              Last:{" "}
              {new Date(lastRetry).toLocaleString("en-IN", {
                dateStyle: "short",
                timeStyle: "short",
              })}
            </Typography>
          )}
          {open ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
        </Stack>
      </Stack>

      {/* Body */}
      <Collapse in={open}>
        <Stack spacing={1.5} sx={{ p: 2, bgcolor: "background.paper" }}>
          {allAttempts.map(({ attempt, historyEntry }) =>
            historyEntry ? (
              <AttemptRow key={attempt} entry={historyEntry} />
            ) : (
              <ScheduleRow
                key={attempt}
                attempt={attempt}
                campaign={campaign}
                // Current = next attempt that hasn't fired yet
                isCurrent={attempt === smartRetryCount + 1}
              />
            )
          )}

          {/* Overall summary — only if 2+ resolved */}
          {retryHistory.filter((r) => r.resolved).length >= 2 && (
            <>
              <Divider />
              <Stack direction="row" justifyContent="space-between" px={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Total across all retries
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  {totalRecovered} recovered out of {totalRetried}
                  {totalRetried > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      {" "}
                      ({Math.round((totalRecovered / totalRetried) * 100)}%)
                    </Typography>
                  )}
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default SmartRetryStatus;
